import './blockquote.scss';

import { z } from 'zod';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';

import { Image } from '../../atoms/image/image';

const LinkedSourceWrapper = ({ primaryCTA, body }: FlexibleContentProps) =>
  primaryCTA?.link ? (
    <Link href={primaryCTA.link} title={primaryCTA.title}>
      {body}
    </Link>
  ) : (
    body
  );

const getBlockquoteVariant = ({ quotation }: FlexibleContentProps) => {
  if (quotation?.image) {
    return 'avatar';
  }
  if (quotation?.primaryCTA) {
    return 'linked';
  }
  return 'default';
};

export const BlockquoteProps = FlexibleContentProps.extend({
  size: z.enum(['sm', 'md', 'lg', 'xs', 'inherit']).optional(),
});
export type BlockquoteProps = z.infer<typeof BlockquoteProps>;

export const Blockquote = ({ quotation, size = 'md' }: BlockquoteProps) => {
  const quotationText = `“${quotation?.text}”`;
  return (
    <figure
      className={cn(
        'Blockquote py-snug-xl pl-comfy-md flex flex-col',
        size === 'lg' ? 'gap-comfy-xl' : 'gap-comfy-md',
      )}
    >
      <blockquote
        className={cn(`Blockquote__quotation emphasis emphasis-${size}`)}
      >
        {quotationText}
      </blockquote>
      {(quotation?.attribution || quotation?.title) && (
        <figcaption
          className={cn(
            `Blockquote__citation Blockquote__citation--${getBlockquoteVariant({ quotation })}`,
            quotation?.title && 'has-title',
          )}
        >
          <LinkedSourceWrapper
            primaryCTA={quotation?.primaryCTA}
            body={
              <div className="flex gap-snug-xl items-center">
                {quotation?.image?.url && (
                  <Image
                    image={quotation?.image}
                    rounded="full"
                    aspectRatio="1"
                    className="mr-snug-sm"
                  />
                )}
                {quotation?.primaryCTA?.link && (
                  <ArkIcon iconName="link-01" size="1.5rem" />
                )}
                <div className="label-moderate label-lg">
                  <div className="Blockquote__attribution">
                    {quotation?.attribution}
                  </div>
                  {quotation?.title && <div>{quotation?.title}</div>}
                </div>
              </div>
            }
          />
        </figcaption>
      )}
    </figure>
  );
};
